import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, 
  Card, 
  Typography, 
  Space, 
  Button, 
  message, 
  Popconfirm,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Statistic
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { announcementService } from '../../services/announcementService';
import { useAuth } from '../../contexts/AuthContext';
import { Announcement } from '../../types';
import { showLoadError, showDeleteError } from '../common/ErrorMessage';
import { useLoading } from '../../hooks/useLoading';
import { useTranslation } from 'react-i18next';


const { Title } = Typography;
const { Option } = Select;

/**
 * 공지사항 목록 컴포넌트
 * 
 * 이 컴포넌트는 공지사항의 목록을 표시하고, 관리자가 공지사항을 관리할 수 있는 기능을 제공합니다.
 * 
 * 주요 기능:
 * - 공지사항 목록 조회 및 표시
 * - 공지사항 생성, 수정, 삭제
 * - 승인/거부 처리
 * - 검색 및 필터링
 * - 페이지네이션
 * 
 * @component
 */
const AnnouncementList: React.FC = () => {
  // 상태 관리
  const [announcements, setAnnouncements] = useState<Announcement[]>([]); // 공지사항 목록
  const [searchText, setSearchText] = useState(''); // 검색 텍스트
  const [statusFilter, setStatusFilter] = useState<string>('all'); // 상태 필터 (all, approved, pending)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]); // 선택된 행들의 키

  const { isAdmin } = useAuth(); // 인증 정보 (관리자 여부)
  const navigate = useNavigate(); // 페이지 이동을 위한 훅
  const { loading, withLoading } = useLoading(false); // 로딩 상태 관리
  const { t } = useTranslation();

  /**
   * 공지사항 목록을 서버에서 가져오는 함수
   * 
   * @async
   * @function fetchAnnouncements
   * @returns {Promise<void>}
   */
  const fetchAnnouncements = useCallback(async () => {
    try {
      const data = await announcementService.getAllAnnouncements(); // API 호출
      setAnnouncements(data); // 상태 업데이트
    } catch (error: any) {
      showLoadError(t('announcements.list')); // 에러 메시지 표시
    }
  }, [t]);

  /**
   * 컴포넌트 마운트 시 공지사항 목록을 가져옴
   */
  useEffect(() => {
    withLoading(fetchAnnouncements);
  }, [fetchAnnouncements, withLoading]);

  /**
   * 검색 및 필터링된 공지사항 목록을 반환하는 함수
   * 
   * @function getFilteredAnnouncements
   * @returns {Announcement[]} 필터링된 공지사항 목록
   */
  const getFilteredAnnouncements = (): Announcement[] => {
    let filtered = announcements;

    // 검색 텍스트로 필터링
    if (searchText) {
      filtered = filtered.filter(announcement =>
        announcement.title.toLowerCase().includes(searchText.toLowerCase()) ||
        announcement.content.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // 상태별 필터링
    if (statusFilter === 'approved') {
      filtered = filtered.filter(announcement => announcement.is_approved);
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(announcement => !announcement.is_approved);
    }

    return filtered;
  };

  // 필터링된 공지사항 목록
  const filteredAnnouncements = getFilteredAnnouncements();

  /**
   * 공지사항 삭제 처리 함수
   * 
   * @async
   * @function handleDelete
   * @param {string} id - 삭제할 공지사항의 ID
   * @returns {Promise<void>}
   */
  const handleDelete = async (id: string) => {
    try {
      await announcementService.deleteAnnouncement(id); // API 호출
      message.success(t('announcements.deleteSuccess')); // 성공 메시지
      withLoading(fetchAnnouncements); // 목록 새로고침
    } catch (error: any) {
      showDeleteError(t('announcements.title')); // 에러 메시지
    }
  };

  /**
   * 공지사항 승인 처리 함수
   * 
   * @async
   * @function handleApprove
   * @param {string} id - 승인할 공지사항의 ID
   * @returns {Promise<void>}
   */
  const handleApprove = async (id: string) => {
    try {
      await announcementService.approveAnnouncement(id); // API 호출
      message.success(t('announcements.approveSuccess')); // 성공 메시지
      fetchAnnouncements(); // 목록 새로고침
    } catch (error: any) {
      message.error(error.response?.data?.message || t('announcements.approveFailed')); // 에러 메시지
    }
  };

  /**
   * 공지사항 승인 취소 처리 함수
   * 
   * @async
   * @function handleUnapprove
   * @param {string} id - 승인 취소할 공지사항의 ID
   * @returns {Promise<void>}
   */
  const handleUnapprove = async (id: string) => {
    try {
      await announcementService.unapproveAnnouncement(id); // API 호출
      message.success(t('announcements.unapproveSuccess')); // 성공 메시지
      fetchAnnouncements(); // 목록 새로고침
    } catch (error: any) {
      message.error(error.response?.data?.message || t('announcements.unapproveFailed')); // 에러 메시지
    }
  };

  /**
   * 일괄 삭제 처리 함수
   * 
   * @async
   * @function handleBatchDelete
   * @returns {Promise<void>}
   */
  const handleBatchDelete = async () => {
    try {
      // 선택된 모든 공지사항을 삭제
      await Promise.all(
        selectedRowKeys.map(key => announcementService.deleteAnnouncement(key.toString()))
      );
      message.success(t('announcements.batchDeleteSuccess', { count: selectedRowKeys.length })); // 성공 메시지
      setSelectedRowKeys([]); // 선택 초기화
      withLoading(fetchAnnouncements); // 목록 새로고침
    } catch (error: any) {
      showDeleteError(t('announcements.title')); // 에러 메시지
    }
  };

  /**
   * 테이블 컬럼 정의
   * 
   * @type {Array} columns - 테이블 컬럼 설정 배열
   */
  const columns = [
    // 선택 체크박스 컬럼
    {
      title: t('announcements.table.selection'),
      dataIndex: 'selection',
      key: 'selection',
      width: 60,
      align: 'center' as const,
      render: (_: any, record: Announcement) => (
        <input
          type="checkbox"
          checked={selectedRowKeys.includes(record.announcement_id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, record.announcement_id]);
            } else {
              setSelectedRowKeys(selectedRowKeys.filter(key => key !== record.announcement_id));
            }
          }}
        />
      ),
    },
    // 제목 컬럼
    {
      title: t('announcements.table.title'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: false,
      align: 'left' as const,
      render: (text: string, record: Announcement) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/announcements/${record.announcement_id}`)}
          style={{ 
            padding: 0, 
            height: 'auto', 
            textAlign: 'left',
            width: '100%',
            overflow: 'visible',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            lineHeight: '1.3',
            fontSize: '14px',
            justifyContent: 'flex-start',
            display: 'flex'
          }}
          title={text}
        >
          {text}
        </Button>
      ),
    },

    // 작성일 컬럼
    {
      title: t('announcements.table.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      align: 'center' as const,
      render: (date: string) => {
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('ko-KR');
      },
    },
    // 승인 상태 컬럼
    {
      title: t('announcements.table.status'),
      dataIndex: 'is_approved',
      key: 'is_approved',
      width: 100,
      align: 'center' as const,
      render: (isApproved: boolean) => (
        <Tag color={isApproved ? 'green' : 'orange'}>
          {isApproved ? t('announcements.table.approved') : t('announcements.table.pending')}
        </Tag>
      ),
    },
    // 조회수 컬럼
    {
      title: t('announcements.table.viewCount'),
      dataIndex: 'view_count',
      key: 'view_count',
      width: 80,
      align: 'center' as const,
      render: (count: number) => count || 0,
    },
    // 댓글 수 컬럼
    {
      title: t('announcements.table.comments'),
      dataIndex: 'comment_count',
      key: 'comment_count',
      width: 80,
      align: 'center' as const,
      render: (count: number) => (
        <Space>
          <MessageOutlined />
          {count || 0}
        </Space>
      ),
    },
    // 작업 컬럼 (관리자만 표시)
    ...(isAdmin ? [{
      title: t('announcements.table.actions'),
      key: 'actions',
      width: 200,
      align: 'center' as const,
      render: (record: Announcement) => (
        <Space size="small">
          {/* 상세보기 버튼 */}
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/announcements/${record.announcement_id}`)}
            size="small"
          >
            {t('announcements.table.view')}
          </Button>
          {/* 수정 버튼 (승인되지 않은 경우에만 표시) */}
          {!record.is_approved && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/announcements/${record.announcement_id}/edit`)}
              size="small"
            >
              수정
            </Button>
          )}
          {/* 승인/거부 버튼 */}
          {record.is_approved ? (
            <Button
              type="link"
              danger
              onClick={() => handleUnapprove(record.announcement_id)}
              size="small"
            >
              {t('announcements.table.unapprove')}
            </Button>
          ) : (
            <Button
              type="link"
              onClick={() => handleApprove(record.announcement_id)}
              size="small"
            >
              {t('announcements.table.approve')}
            </Button>
          )}
          {/* 삭제 버튼 */}
          <Popconfirm
            title={t('announcements.table.deleteConfirm')}
            onConfirm={() => handleDelete(record.announcement_id)}
            okText={t('announcements.table.delete')}
            cancelText={t('common.cancel')}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              {t('announcements.table.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div>
      {/* 헤더 섹션 */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          {/* 제목 */}
          <Title level={3} style={{ margin: 0 }}>
            {t('announcements.management')}
          </Title>
          
          {/* 관리자용 버튼들 */}
          {isAdmin && (
            <Space>
              {/* 새 공지사항 작성 버튼 */}
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => navigate('/announcements/create')}
              >
                {t('announcements.newAnnouncement')}
              </Button>
              
              {/* 일괄 삭제 버튼 (선택된 항목이 있을 때만 표시) */}
              {selectedRowKeys.length > 0 && (
                <Popconfirm
                  title={t('announcements.table.batchDeleteConfirm', { count: selectedRowKeys.length })}
                  onConfirm={handleBatchDelete}
                  okText={t('announcements.table.delete')}
                  cancelText={t('common.cancel')}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    {t('announcements.batchDelete')}
                  </Button>
                </Popconfirm>
              )}
            </Space>
          )}
        </div>

        {/* 검색 및 필터 섹션 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            {/* 검색 입력창 */}
            <Input
              placeholder={t('announcements.searchPlaceholder')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={6}>
            {/* 상태 필터 드롭다운 */}
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">{t('announcements.statusFilter.all')}</Option>
              <Option value="approved">{t('announcements.statusFilter.approved')}</Option>
              <Option value="pending">{t('announcements.statusFilter.pending')}</Option>
            </Select>
          </Col>
          <Col span={6}>
            {/* 새로고침 버튼 */}
            <Button 
              onClick={fetchAnnouncements} 
              loading={loading}
              style={{ width: '100%' }}
            >
              {t('announcements.refresh')}
            </Button>
          </Col>
        </Row>

        {/* 통계 정보 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic title={t('announcements.statistics.total')} value={announcements.length} />
          </Col>
          <Col span={6}>
            <Statistic 
              title={t('announcements.statistics.approved')} 
              value={announcements.filter(a => a.is_approved).length}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title={t('announcements.statistics.pending')} 
              value={announcements.filter(a => !a.is_approved).length}
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title={t('announcements.statistics.approvalRate')} 
              value={announcements.length > 0 ? 
                Math.round((announcements.filter(a => a.is_approved).length / announcements.length) * 100) : 0
              }
              suffix="%" 
            />
          </Col>
        </Row>

        {/* 테이블 */}
        <Table
          columns={columns}
          dataSource={filteredAnnouncements}
          rowKey="announcement_id"
          loading={loading}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('pagination.showTotal', { 
              start: range[0], 
              end: range[1], 
              total: total 
            }),
          }}
          scroll={{ x: 900 }}
          size="small"
          style={{
            fontSize: '14px'
          }}
          className="announcement-table"
        />
        
        {/* 제목 컬럼 최소 너비 설정을 위한 CSS */}
        <style>{`
          .announcement-table .ant-table-thead > tr > th:nth-child(2),
          .announcement-table .ant-table-tbody > tr > td:nth-child(2) {
            min-width: 200px !important;
          }
        `}</style>
      </Card>
    </div>
  );
};

export default AnnouncementList; 